// src/lib/constants.ts
/** Mathematical constants for T-11 overlay functionality */

export interface MathConstant {
    id: string;
    name: string;
    symbol: string;
    value: () => number;
    defaultPrecision: number;
    description?: string;
  }
  
  export const MATHEMATICAL_CONSTANTS: MathConstant[] = [
    {
      id: 'sqrt2',
      name: 'Square Root of 2',
      symbol: '√2',
      value: () => Math.SQRT2,
    defaultPrecision: 3,
      description: 'The positive square root of 2, approximately 1.414'
    },
    {
      id: 'sqrt3',
      name: 'Square Root of 3',
      symbol: '√3',
      value: () => Math.sqrt(3),
    defaultPrecision: 3,
      description: 'The positive square root of 3, approximately 1.732'
    },
    {
      id: 'sqrt5',
      name: 'Square Root of 5',
      symbol: '√5',
      value: () => Math.sqrt(5),
    defaultPrecision: 3,
      description: 'The positive square root of 5, approximately 2.236'
    },
    {
      id: 'sqrt10',
      name: 'Square Root of 10',
      symbol: '√10',
      value: () => Math.sqrt(10),
    defaultPrecision: 3,
      description: 'The positive square root of 10, approximately 3.162'
    },
    {
      id: 'pi',
      name: 'Pi',
      symbol: 'π',
      value: () => Math.PI,
    defaultPrecision: 3,
      description: 'The ratio of a circle\'s circumference to its diameter'
    },
    {
      id: 'tau',
      name: 'Tau',
      symbol: 'τ',
      value: () => Math.PI * 2,
    defaultPrecision: 3,
      description: 'Tau equals 2π, the ratio of a circle\'s circumference to its radius'
    }
  ];
  
  /**
   * Get constants that fall within the specified domain range
   */
  export function getVisibleConstants(
    domain: { start: number; end: number },
    constants: MathConstant[] = MATHEMATICAL_CONSTANTS
  ): MathConstant[] {
    return constants.filter(constant => {
      const value = constant.value();
      return value >= domain.start && value <= domain.end;
    });
  }
  
  /**
   * Format a constant value with specified precision
   */
  export function formatConstantValue(constant: MathConstant, precision: number): string {
    return constant.value().toFixed(precision);
  }
  
  /**
   * Create display label for constant (symbol + value)
   */
  export function createConstantLabel(constant: MathConstant, precision: number): string {
    return `${constant.symbol} ${formatConstantValue(constant, precision)}`;
  }
  
  /**
   * Calculate collision-free positions for constant labels
   * Returns array of { constant, x, y, offsetY } objects
   */
  export function calculateConstantPositions(
    visibleConstants: MathConstant[],
    valueToPixel: (value: number) => number,
    isHorizontal: boolean,
    lineX: number,
    lineY: number,
    labelHeight: number = 20,
    minSpacing: number = 60
  ): Array<{
    constant: MathConstant;
    x: number;
    y: number;
    offsetY: number;
  }> {
    const positions = visibleConstants.map(constant => ({
      constant,
      value: constant.value(),
      pixelPos: valueToPixel(constant.value())
    }));
  
    // Sort by pixel position
    positions.sort((a, b) => a.pixelPos - b.pixelPos);
  
    const result: Array<{
      constant: MathConstant;
      x: number;
      y: number;
      offsetY: number;
    }> = [];
  
    let lastPosition = -Infinity;
    let currentLane = 0;
  
    for (const pos of positions) {
      // Check if we need to move to next lane due to collision
      if (pos.pixelPos - lastPosition < minSpacing && result.length > 0) {
        currentLane++;
      } else {
        currentLane = 0;
      }
  
      const offsetY = currentLane * labelHeight;
  
      if (isHorizontal) {
        result.push({
          constant: pos.constant,
          x: pos.pixelPos,
          y: lineY - 40 - offsetY, // Position above the line with offset
          offsetY
        });
      } else {
        result.push({
          constant: pos.constant,
          x: lineX + 40 + offsetY, // Position to the right with offset
          y: pos.pixelPos,
          offsetY
        });
      }
  
      lastPosition = pos.pixelPos;
    }
  
    return result;
  }